import type { Finding } from '../api/types'
import { deltaTone, formatChange, formatMetric, metricLabel } from '../format'

interface Props {
  findings: Finding[]
  minimumSegmentUsers: number
  minimumSegmentSharePercent: number
  thresholdPercent: number
  findingsMetric: string
}

const ARROW = { up: '↑', down: '↓', flat: '→' } as const

/** Segments whose movement cleared the anomaly threshold, largest first. */
export function Findings({
  findings,
  minimumSegmentUsers,
  minimumSegmentSharePercent,
  thresholdPercent,
  findingsMetric,
}: Props) {
  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">What changed</h2>
        <span className="section__note">
          Ranked by {metricLabel(findingsMetric).toLowerCase()} · movements over {thresholdPercent}% ·
          segments under {minimumSegmentSharePercent}% of total or {minimumSegmentUsers} users
          suppressed
        </span>
      </div>

      {findings.length === 0 ? (
        <div className="card">
          <p className="placeholder" style={{ padding: '20px 0' }}>
            No channel moved more than {thresholdPercent}% in this window.
          </p>
        </div>
      ) : (
        <div className="findings">
          {findings.map((finding) => {
            const tone = deltaTone(finding.change_percent, finding.metric)
            return (
              <article className="finding" key={`${finding.metric}-${finding.segment}`}>
                <div>
                  <div className="finding__segment">{finding.segment}</div>
                  <div className="finding__detail">
                    {metricLabel(finding.metric)}{' '}
                    {formatMetric(finding.metric, finding.previous, 'full')} →{' '}
                    {formatMetric(finding.metric, finding.current, 'full')}
                  </div>
                </div>
                <div className={`finding__change tile__delta--${tone}`}>
                  <span aria-hidden="true">{ARROW[tone]}</span>
                  {formatChange(finding.change_percent)}
                </div>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
